import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  CloudArrowDownIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/16/solid";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useReports } from "./hooks/useReports";
import { useDownloadReport } from "./hooks/useDownloadReport";
import { ReportCard } from "./report-card";
import { useParams } from "react-router-dom";
import { routes } from "../../../routing/routes";
import { Pagination } from "../../../components/Pagination";
import { DownloadModal } from "./components/download-modal";
import { DownloadReportResult } from "./hooks/useDownloadReport";

const PAGE_SIZE = 50;

export const DashboardContent: React.FC = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [modalReportData, setModalReportData] =
    useState<DownloadReportResult | null>(null);
  const { data, isLoading, error } = useReports(currentPage, pageSize);
  const downloadReport = useDownloadReport();
  const { id } = useParams();

  useEffect(() => {
    if (!id) {
      return;
    }

    handleDownload(id);

    window.history.replaceState(null, "", routes.dashboard);
  }, [id]);

  const reports = useMemo(() => data?.reports || [], [data?.reports]);

  const handleDownload = useCallback(
    async (reportId: string) => {
      try {
        const result = await downloadReport.downloadReportAsync({
          reportId,
        });
        setModalReportData(result);
      } catch (error) {
        Sentry.captureException(error);
        toast.error(t("dashboard.downloadError"));
      }
    },
    [downloadReport, t],
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (isLoading) {
    return (
      <div
        className="space-y-3"
        role="status"
        aria-label={t("dashboard.loading")}
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="flex flex-col md:flex-row md:items-center gap-3 p-3 md:p-4 border rounded-lg bg-gray-50 animate-pulse"
          >
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div className="h-9 w-20 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-8 space-y-4">
        <div className="text-red-600 text-center">
          <ExclamationCircleIcon className="h-8 w-8 mx-auto mb-2" />
          <p className="text-lg font-semibold">{t("dashboard.error")}</p>
          <p className="text-sm text-gray-600 mt-1">
            {error instanceof Error
              ? error.message
              : t("dashboard.unexpectedError")}
          </p>
        </div>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="flex flex-col items-center py-12 space-y-4">
        <div className="text-gray-400">
          <CloudArrowDownIcon className="h-16 w-16 mx-auto mb-4 text-primary" />
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-600">
            {t("dashboard.noReports")}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {t("dashboard.noReportsDescription")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 space-between">
      {data && data.total > 0 && (
        <div className="text-sm text-gray-600">
          {t("dashboard.pagination.showing", {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, data.total),
            total: data.total,
          })}
        </div>
      )}

      <div className="space-y-3">
        {reports.map((report) => (
          <ReportCard
            key={report.id}
            report={report}
            onDownload={handleDownload}
          />
        ))}
      </div>

      {data && data.totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={data.totalPages}
          onPageChange={handlePageChange}
          className="mt-8"
        />
      )}

      {modalReportData && (
        <DownloadModal
          reportData={modalReportData}
          onClose={() => setModalReportData(null)}
        />
      )}
    </div>
  );
};
